import { Dispatch } from 'redux';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { AsyncJob, AsyncJobService, AsyncJobStatus, AsyncJobType } from 'types';
import { selectNotifications } from './selectors';
import { RootState } from '../reducer';

type NotificationState = {
  notifications: AsyncJob[],
};

const initialState: NotificationState = {
  notifications: [],
};

export const getJobTitleByType = (type: AsyncJobType) => {
  if (type === AsyncJobType.CLEANUP_CS_ADMIN_TENANCIES) {
    return type;
  }

  switch (type) {
  case AsyncJobType.DATA_LOADER:
    return 'Data loading';
  case AsyncJobType.RECALCULATE_AGING_FACTOR:
    return 'Aging factor recalculation';
  case AsyncJobType.RECALCULATE_COMPOSITE_TAG:
    return 'Composite tag recalculation';
  case AsyncJobType.RECALCULATE_COMPOSITES:
    return 'Composite recalculation';
  case AsyncJobType.RECALCULATE_PLAN:
    return 'Plan recalculation';
  case AsyncJobType.UPDATE_CUSTOM_PERCENTILE:
    return 'Custom Percentile recalculation';
  case AsyncJobType.ADD_INDEXED_DATA_CUTS:
    return 'Indexed Composites recalculation';
  case AsyncJobType.REMOVE_INDEXED_DATA_CUTS:
    return 'Indexed Composites recalculation';
  case AsyncJobType.RESET_DEFAULT_CUTS:
    return 'Applying Default Cuts';
  case AsyncJobType.UPDATE_PLAN:
    return 'Updating plan';
  case AsyncJobType.EXPORT_DATA:
    return 'Data Exporting'
  case AsyncJobType.EXPORT_EXCEL:
    return 'Exporting Survey Participation';
  case AsyncJobType.MIGRATE_PLAN:
    return 'Create plan';
  case AsyncJobType.ACTIVATE_DRAFT_PLAN:
    return 'Plan activation';
  case AsyncJobType.CREATE_PAYMARKET:
    return 'Create PayMarket';
  case AsyncJobType.DELETE_LOAD:
    return 'Delete File';
  case AsyncJobType.DELETE_SURVEY:
    return 'Delete Survey';
  case AsyncJobType.RECALCULATE_COMPANY_JOB_STATS:
    return 'Company Job Stats recalculation';
  case AsyncJobType.RECALCULATE_PLAN_STATS:
    return 'Plan Stats recalculation';
  case AsyncJobType.RECALCULATE_SLOT_JOB:
    return 'Slot Job recalculation';
  case AsyncJobType.RECALCULATE_SURVEY_CUT_STATS:
    return 'Survey Cut Stats recalculation';
  case AsyncJobType.RECALCULATE_SURVEY_JOB_STATS:
    return 'Survey Job Stats recalculation';
  case AsyncJobType.SETUP_COMPANY:
    return 'Company Is Setting Up';
  case AsyncJobType.RECALCULATE_DATA_MODEL_COMPOSITE_STATS:
  case AsyncJobType.RECALCULATE_DATA_MODEL_WITH_AUTO_SPREAD:
    return 'Data Model Recalculation';
  case AsyncJobType.PUBLISH_SURVEY_REPLACEMENT:
    return 'Survey Replacement publishing';
  case AsyncJobType.APPLY_CUT_SELECTION_POLICY_SURVEY_REPLACEMENT:
    return 'Survey Replacement cuts update';
  case AsyncJobType.ACTUALIZE_COMPOSITE_DATA_CUT_REPLACEMENT:
    return 'Survey Replacement composite data cuts actualizing';
  case AsyncJobType.ACTUALIZE_SURVEY_JOB_MAPPING_REPLACEMENT:
    return 'Survey Replacement job mappings actualizing';
  case AsyncJobType.INITIATE_RECALCULATION:
    return 'Composites recalculation';
  case AsyncJobType.SYNC_DATAHUB:
    return 'Survey Data synchronization';
  case AsyncJobType.TEST_HRIS_URL:
    return 'HRIS URL Test';
  case AsyncJobType.GENERATE_JOB_DESCRIPTION:
    return 'Job Description';
  case AsyncJobType.APPLY_WEIGHTING_POLICY:
    return 'Apply Weighting Policy';
  case AsyncJobType.ADD_DEFAULT_DATA_CUTS_FOR_JOB_MAPPINGS:
    return 'Add default data mapping';
  case AsyncJobType.GET_SURVEY_DATA_BY_POLICY_RESPONSE:
    return 'Get Survey Data By Policy';
  default:
    // If there is ts error, ensure all types handled by switch case
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const exhaustiveCheck: never = type;
    return type;
  }
};

export const setNotifications = createAction<AsyncJob[]>('NOTIFICATIONS/SET_NOTIFICATIONS');

export const addNotificationAction = createAction<AsyncJob>('NOTIFICATIONS/ADD_NOTIFICATION');

export const removeNotificationAction = createAction<number>('NOTIFICATIONS/REMOVE_NOTIFICATION');

export const updateNotificationAction = createAction<AsyncJob>('NOTIFICATIONS/UPDATE_NOTIFICATION');

export const reloadNotificationAction = createAction<AsyncJob[]>('NOTIFICATIONS/RELOAD_NOTIFICATIONS');

export const addNotification = (notification: AsyncJob) => (dispatch: Dispatch) => {
  dispatch(addNotificationAction(notification));
};

export const updateNotification = (notification: AsyncJob) => (dispatch: Dispatch) => {
  dispatch(updateNotificationAction(notification));
};

export const removeNotification = (notification: AsyncJob) => (dispatch: Dispatch) => {
  dispatch(removeNotificationAction(notification.id));
};

export const reloadNotifications = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const notifications = selectNotifications(getState());

  if (notifications.length) {
    const rows = await AsyncJobService.getAsyncJobList({
      status: [AsyncJobStatus.SCHEDULED, AsyncJobStatus.IN_PROGRESS],
      ignorePagination: true,
    });

    dispatch(reloadNotificationAction(rows));
  }
};

export const notificationsReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setNotifications, (state, action) => {
      state.notifications = action.payload;
    })
    .addCase(reloadNotificationAction, (state, action) => {
      state.notifications = state.notifications.filter(notification => {
        return action.payload.find(({ id }) => id === notification.id);
      });
    })
    .addCase(addNotificationAction, (state, action) => {
      const existingNotification = state.notifications.find(notification => notification.id === action.payload.id);
      if (!existingNotification) {
        state.notifications = [
          action.payload,
          ...state.notifications,
        ];
      }
    })
    .addCase(removeNotificationAction, (state, action) => {
      state.notifications = state.notifications.filter(notification => notification.id !== action.payload);
    })
    .addCase(updateNotificationAction, (state, action) => {
      const existingNotification = state.notifications.find(notification => notification.id === action.payload.id);

      state.notifications = existingNotification
        ? state.notifications.map(notification => {
          if (notification === existingNotification) {
            return action.payload;
          }

          return notification;
        })
        : [action.payload, ...state.notifications];
    })
  ;
});
